import { Observable, Subject as RxjsSubject } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { platform } from "../../../../../platform/platform";
import { ApplicationError } from "../../../../_errors/application-error";
import { InspectionReport, InspectionReportListItem, ReportTimer } from "../../../../_objects/inspection-report";
import { UtilsService } from "../../../../_services/_utils.service";
import { CommonSnackBarService } from "../../../../_services/common-snack-bar.service";
import { InspectionReportService } from "../../../../_services/http-api/inspection-report.service";
import { ReportTemplateService } from "../../../../_services/http-api/report-template.service";
import { TenantConfigService } from "../../../../_services/http-api/tenant-config.service";
import { LangUpdateService } from "../../../../_services/lang-update.service";
import { NetworkStatusService } from "../../../../_services/network-status.service";
import { SpinnerService } from "../../../../_services/spinner.service";
import { BROWSER_TYPE, INSPECTION_REPORT_STATUS_CODE, PLATFORM, TenantConfig } from "../../../../app.constants";
import { CommonConfirmDialogComponent } from "../../../../common-dialog/common-confirm-dialog.component";
import { CommonShareDialogComponent } from "../../../../common-dialog/common-share-dialog/common-share-dialog.component";
import { InspectionReportItemWrapper } from "../../../../inspection-report-list/inspection-report-list.component";
import { MeasurementModeName } from "../../../../_objects/measurement-mode-name";
import { InspectionDataManageService } from "../../../inspection-data-manage.service";
import { getFormatedExpirationDate } from "../../../../_utils/date.utils";

@Component({
  selector: "app-inspection-report-history",
  templateUrl: "./inspection-report-history.component.html",
  styleUrls: ["./inspection-report-history.component.scss"],
  standalone: false
})
export class InspectionReportHistoryComponent implements OnDestroy, OnChanges {
  @Input() targetReport: InspectionReport;
  @Input() reports: InspectionReportListItem[];
  @Input() isReportLoading: boolean;

  readonly getReportTime = 10000;
  reportTimers: ReportTimer[];
  onDestroy$ = new RxjsSubject();
  browserName: string;
  isAdmin = false;
  private measurementModeName: MeasurementModeName;

  panelClass = "custom-snackbar";

  get canReuseData(): boolean {
    if (this.utilsService.isNullOrUndefined(this.targetReport)) {
      return false;
    }
    return this.isReportStatusEditing(this.targetReport.status);
  }

  get isDesktopAndOffline() {
    return this.networkStatusService.isDesktopAndOffline;
  }

  constructor(
    private utilsService: UtilsService,
    private inspectionReportService: InspectionReportService,
    private reportTemplateService: ReportTemplateService,
    private snackBar: CommonSnackBarService,
    private inspDataManageService: InspectionDataManageService,
    private networkStatusService: NetworkStatusService,
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private tenantConfigService: TenantConfigService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    this.measurementModeName = new MeasurementModeName(this.translate);
  }

  ngOnDestroy(): void {
    this.reportTimers.forEach((reportTimer) => clearTimeout(reportTimer.timer));
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.reports && this.reports && !this.reportTimers) {
      this.reportTimers = [];
      this.reports
        .filter((res) => res.status === INSPECTION_REPORT_STATUS_CODE.DRAFT_PROCESSING)
        .forEach((report) => {
          this.reportTimers.push({ reportId: report.reportId, timer: null });
        });

      this.reportTimers.forEach((reportTime) => this.getAndSetReport(reportTime.reportId));
    }
  }

  reuseEmit(reportId: string) {
    this.inspDataManageService.reuseReportEmit(reportId);
  }

  downloadPdf(report: InspectionReportListItem) {
    if (!this.isReportStatusEditing(report.status)) {
      this.downloadReportPdf(report);
    } else {
      this.downloadDraftReportPdf(report);
    }
  }

  openShareDialog(report: InspectionReportItemWrapper): Observable<any> {
    this.oscSpinnerService.show(true);
    const dialogRef = this.dialog.open(CommonShareDialogComponent, {
      width: "800px",
      minWidth: "400px",
      maxHeight: "800px",
      data: { report, isSummary: false }
    });
    return dialogRef.afterClosed();
  }

  shareDraftInspectionReportPdf(report: InspectionReportItemWrapper) {
    this.inspectionReportService
      .getInspectionReportSharedLink(report.reportId, getFormatedExpirationDate(), null, report.status === "000" ? true : false)
      .subscribe(
        (_) => {},
        (error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.getInspectionReport(report);
          } else {
            console.log(error);
            this.snackBar.open(this.translate.instant("inspectionReportHistory.shareDraftInspectionReportPdfError"));
          }
        },
        () => {
          this.openShareDialog(report);
        }
      );
  }

  private downloadReportPdf(report: InspectionReportListItem): void {
    if (!report || !report.reportId || !report.title) {
      throw new ApplicationError("report is Invalid.");
    }

    if (this.isReportStatusEditing(report.status)) {
      return;
    }

    if (this.utilsService.getBrowserName() === BROWSER_TYPE.SAFARI) {
      window.open("pdf-load", "child" + report.reportId);
    }

    this.inspectionReportService.getInspectionReportPdf(report.reportId).subscribe(
      (blob) => this.utilsService.download(blob, report.title + ".pdf", report.reportId),
      (error) => {
        console.log(error);
        this.snackBar.open(this.translate.instant("inspectionReportHistory.downloadReportPdfError"), this.panelClass);
      }
    );
  }

  private downloadDraftReportPdf(report: InspectionReportListItem) {
    if (!report.reportId || report.reportId === null || report.reportId === undefined) {
      throw new ApplicationError("reportId is null.");
    }

    if (this.browserName === BROWSER_TYPE.SAFARI) {
      window.open("pdf-load", "child" + report.reportId);
    }

    this.inspectionReportService.getInspectionReportPdf(report.reportId, null, true).subscribe(
      (blob) => {
        this.utilsService.download(blob, report.title + ".pdf", report.reportId);
      },
      (error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.getInspectionReport(report);
        } else {
          this.snackBar.open(this.translate.instant("inspectionReportHistory.downloadDraftReportPdfError"), this.panelClass);
        }
      }
    );
  }

  private isDraftProcessing(status: string): boolean {
    return status === INSPECTION_REPORT_STATUS_CODE.DRAFT_PROCESSING;
  }

  private isFinalProcessing(status: string): boolean {
    return status === INSPECTION_REPORT_STATUS_CODE.FINAL_PROCESSING;
  }

  private isReportStatusEditing(status: string): boolean {
    return status === INSPECTION_REPORT_STATUS_CODE.EDITING;
  }

  private getInspectionReport(report: InspectionReportListItem) {
    return this.inspectionReportService.getInspectionReport(report.reportId).subscribe(
      (data) => {
        this.updateDraftPdf(data);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  private updateDraftPdf(inspectionReport: InspectionReport) {
    this.createDraftPdfConfirmDialog();

    const draftQuality = this.tenantConfigService.tenantConfig.find((_) => _.key === TenantConfig.DefaultImageQualityOfDraftReports.KEY).value;
    this.reportTimers.push({ reportId: inspectionReport.reportId, timer: undefined });

    this.measurementModeName.objectDistance = this.translate.instant("imageRemeasurementEdit.objectDistance");
    this.measurementModeName.distance = this.translate.instant("imageRemeasurementEdit.distance");
    this.measurementModeName.pointToLine = this.translate.instant("imageRemeasurementEdit.pointToLine");
    this.measurementModeName.depth = this.translate.instant("imageRemeasurementEdit.depth");
    this.measurementModeName.lines = this.translate.instant("imageRemeasurementEdit.lines");
    this.measurementModeName.area = this.translate.instant("imageRemeasurementEdit.area");

    return this.reportTemplateService.getReportTemplate(inspectionReport.templateId).subscribe((reportTemplate) => {
      const pdfOptions = this.inspectionReportService.getPdfOptions(Number(draftQuality), true, reportTemplate.template.paperSize);
      const updateReport = this.reports.find((report) => report.reportId === inspectionReport.reportId);
      updateReport.status = INSPECTION_REPORT_STATUS_CODE.DRAFT_PROCESSING;

      this.inspectionReportService.createReportPdfAsync(inspectionReport.reportId, pdfOptions, this.measurementModeName, true).subscribe(
        (report) => {
          this.setReport(report);
        },
        (error) => {
          console.log(error);
          updateReport.status = INSPECTION_REPORT_STATUS_CODE.FAILED;
          this.snackBar.open(this.translate.instant("inspectionReport.FailSave"));
        }
      );
    });
  }

  private setReport(inspectionReport: InspectionReport): void {
    const target = this.reportTimers.find((res) => res.reportId === inspectionReport.reportId);
    if (target) {
      clearTimeout(target.timer);
    }

    switch (inspectionReport.status) {
      case INSPECTION_REPORT_STATUS_CODE.DRAFT_PROCESSING:
      case INSPECTION_REPORT_STATUS_CODE.FINAL_PROCESSING:
        if (target) {
          target.timer = setTimeout(() => this.getAndSetReport(target.reportId), this.getReportTime);
        }
        break;

      case INSPECTION_REPORT_STATUS_CODE.EDITING:
        if (this.isDraftProcessing(inspectionReport.status)) {
          this.reportTimers = this.reportTimers.filter((report) => report.reportId !== inspectionReport.reportId);
          const message = this.translate.instant("inspectionReportHistory.setDraftReportSuccessfully", {
            inspectionReportTitle: inspectionReport.title
          });
          this.snackBar.open(message, this.panelClass);
          const report = this.reports.find((report) => report.reportId === inspectionReport.reportId);
          this.openShareDialog(report);
        }
        break;

      case INSPECTION_REPORT_STATUS_CODE.COMPLETED:
        if (this.isFinalProcessing(inspectionReport.status)) {
          this.reportTimers = this.reportTimers.filter((report) => report.reportId !== inspectionReport.reportId);
          const message = this.translate.instant("inspectionReportHistory.setFinalReportSuccessfully", {
            inspectionReportTitle: inspectionReport.title
          });
          this.snackBar.open(message, this.panelClass);
        }
        break;

      case INSPECTION_REPORT_STATUS_CODE.FAILED:
        if (this.isDraftProcessing(inspectionReport.status)) {
          this.reportTimers = this.reportTimers.filter((report) => report.reportId !== inspectionReport.reportId);
          const message = this.translate.instant("inspectionReportHistory.setReportFailed", { inspectionReportTitle: inspectionReport.title });
          this.snackBar.open(message, this.panelClass);
        }
        break;
    }
    const updateReport = this.reports.find((report) => report.reportId === inspectionReport.reportId);
    updateReport.status = inspectionReport.status;
  }

  private getAndSetReport(reportId: string): void {
    if (!reportId || reportId === null || reportId === undefined) {
      throw new ApplicationError("reportId is null.");
    }

    this.inspectionReportService.getInspectionReport(reportId).subscribe(
      (inspectionReport) => this.setReport(inspectionReport),
      (error: HttpErrorResponse) => {
        console.log(error);
        this.snackBar.open(this.translate.instant("inspectionReportHistory.getReportError"), this.panelClass);
      }
    );
  }

  private createDraftPdfConfirmDialog() {
    return this.dialog.open(CommonConfirmDialogComponent, {
      width: "500px",
      height: "230px",
      data: {
        firstMessage: this.translate.instant("inspectionReportHistory.createDraftPdfConfirmDialogFirstMessage"),
        secondMessage: this.translate.instant("inspectionReportHistory.createDraftPdfConfirmDialogSecondMessage"),
        dialogType: "information"
      }
    });
  }
}
